import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import { Switch } from '../ui/switch';
import { FieldLabel, FieldSubLabel } from './FieldParts';
import { useElementValue, useIsReadOnly } from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';

/**
 * Coerce a boolean answer to a boolean, anything else to undefined. Covers
 * the string forms a logic set_value action or an import can persist
 * ("false", "no", "off", 0) so they never read as checked.
 */
const coerceBoolean = (v: unknown): boolean | undefined => {
  if (v === true || v === 1) return true;
  if (v === false || v === 0) return false;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === 'yes' || s === 'on' || s === '1') return true;
    if (s === 'false' || s === 'no' || s === 'off' || s === '0') return false;
  }
  return undefined;
};

interface Props {
  element: DocumentElementType;
}

const ToggleSwitch: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<boolean>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    description?: string;
    required?: boolean;
    defaultValue?: boolean | string;
    hidden?: boolean;
  };
  const displayValue = useDefaultValue(
    coerceBoolean(value),
    setValue,
    coerceBoolean(data?.defaultValue),
  );
  const label = data?.label || 'Toggle';
  const description = data?.description || '';
  const required = data?.required || false;

  // Read-only (e.g. a completed result): render the on/off state as
  // static text — no operable switch.
  if (isReadOnly) {
    const on = displayValue ?? false;
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <FieldLabel htmlFor={element.id} required={required}>
              {label}
            </FieldLabel>
            <FieldSubLabel>{description}</FieldSubLabel>
          </div>
          <p className={on ? 'text-sm font-medium' : 'text-sm text-muted-foreground'}>
            {on ? 'On' : 'Off'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <FieldLabel
            htmlFor={element.id}
            required={required}
            className="cursor-pointer"
          >
            {label}
          </FieldLabel>
          <FieldSubLabel>{description}</FieldSubLabel>
        </div>
        <Switch
          id={element.id}
          checked={displayValue ?? false}
          onCheckedChange={setValue}
        />
      </div>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default ToggleSwitch;
